import * as PopoverPrimitive from '@radix-ui/react-popover';
import styled, { css } from 'styled-components';

import { zIndexElevatedLayer4 } from '@xing-com/layout-tokens';
import {
  cornerRadiusXL,
  dropShadowM,
  motionEasingEnter,
  motionEasingLeave,
  motionTimeS,
  scale050,
  scale100,
  scale130,
  scale420,
  spaceM,
  spaceS,
  spaceXS,
  spaceXXS,
  xdlColorCardInspire04,
  xdlColorSelected,
  xdlColorTextInvert,
  xdlColorTextOnLight,
} from '@xing-com/tokens';

import {
  arrowAnimation,
  fadeIn,
  fadeOut,
  slideDownIn,
  slideDownOut,
  slideLeftIn,
  slideLeftOut,
  slideRightIn,
  slideRightOut,
  slideUpIn,
  slideUpOut,
} from './tip-box.motion';
import type { TipBoxSideProps, TipBoxVariant } from './tip-box.types';

type TipBoxContentTProps = {
  $side?: TipBoxSideProps;
  $variant?: TipBoxVariant;
};

type TipBoxArrowTProps = {
  $variant?: TipBoxVariant;
};

type TipBoxTriggerTProps = {
  $asChild?: boolean;
};

// *** Variants ***
export const variants = {
  default: {
    arrow: css`
      fill: ${xdlColorSelected};
    `,
    content: css`
      background-color: ${xdlColorSelected};
      color: ${xdlColorTextInvert};
    `,
  },
  highlighted: {
    arrow: css`
      fill: ${xdlColorCardInspire04};
    `,
    content: css`
      background-color: ${xdlColorCardInspire04};
      color: ${xdlColorTextOnLight};
    `,
  },
};

// *** Components ***
export const TipBoxRoot = styled(PopoverPrimitive.Root)``;

export const TipBoxTrigger = styled(
  PopoverPrimitive.Trigger
)<TipBoxTriggerTProps>`
  ${({ $asChild }) =>
    !$asChild &&
    css`
      all: unset;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
    `}
`;

export const TipBoxContent = styled(
  PopoverPrimitive.Content
)<TipBoxContentTProps>`
  border-radius: ${cornerRadiusXL};
  box-shadow: ${dropShadowM};
  box-sizing: border-box;
  max-width: ${scale420};
  padding: ${spaceM};
  z-index: ${zIndexElevatedLayer4};

  /* Ensure the animation works properly */
  will-change: transform, opacity;
  transform-origin: var(--radix-popover-content-transform-origin);

  & :is(h1, h2, h3, h4, h5, h6, p) {
    ${({ $variant }) => {
      switch ($variant) {
        case 'default':
          return variants.default.content;
        case 'highlighted':
          return variants.highlighted.content;
        default:
          return $variant satisfies undefined;
      }
    }}
  }

  ${({ $variant }) => {
    switch ($variant) {
      case 'default':
        return variants.default.content;
      case 'highlighted':
        return variants.highlighted.content;
      default:
        return $variant satisfies undefined;
    }
  }}

  ${({ $variant }) => {
    return (
      $variant &&
      css`
        & [data-xds='Marker'] {
          margin-block-end: ${spaceXS};
        }

        & [data-xds='Marker'] [data-xds='BodyCopy'] {
          margin-block-end: 0;
        }

        & [data-xds='Headline'] {
          margin-block-end: ${spaceXXS};
        }

        & [data-xds='BodyCopy'] {
          margin-block-end: ${spaceS};
        }
      `
    );
  }}

  ${({ $side }) => {
    const enter = motionEasingLeave;
    const leave = motionEasingEnter;
    let open = fadeIn;
    let close = fadeOut;

    switch ($side) {
      case 'top': {
        open = slideDownIn;
        close = slideUpOut;
        break;
      }
      case 'bottom': {
        open = slideUpIn;
        close = slideDownOut;
        break;
      }
      case 'left': {
        open = slideRightIn;
        close = slideLeftOut;
        break;
      }
      case 'right': {
        open = slideLeftIn;
        close = slideRightOut;
        break;
      }
    }

    return css`
      &[data-state='open'] {
        animation: ${() => open} ${() => motionTimeS} ${() => enter} forwards;
      }
      &[data-state='closed'] {
        animation: ${() => close} ${() => motionTimeS} ${() => leave} forwards;
      }
    `;
  }}
`;

export const TipBoxArrow = styled(PopoverPrimitive.Arrow)<TipBoxArrowTProps>`
  width: ${scale100};
  height: ${scale050};

  /** Bugfix */
  visibility: visible;

  /* Arrow animation */
  ${arrowAnimation};

  ${({ $variant }) => {
    switch ($variant) {
      case 'default':
        return variants.default.arrow;
      case 'highlighted':
        return variants.highlighted.arrow;
      default:
        return $variant satisfies undefined;
    }
  }}
`;

export const TipBoxClose = styled(PopoverPrimitive.Close)`
  all: unset;
  align-items: center;
  cursor: pointer;
  display: flex;
  height: ${scale130};
  inset-block-start: 0;
  inset-inline-end: 0;
  justify-content: center;
  position: absolute;
  width: ${scale130};

  &:focus-visible {
    outline: 2px solid ${xdlColorTextInvert};
    border-radius: 50%;
  }
`;
